import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, formatApiError, imgUrl } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Upload, X, Leaf, Euro, Weight, Package, Calendar } from 'lucide-react';
import PublishSuccessModal from '@/components/PublishSuccessModal';

const QUALITY_LABEL = {
  standard: 'Standard',
  biologico: 'Biologico',
  igp: 'IGP',
  dop: 'DOP',
  in_conversione: 'In conversione bio',
};
const PACKAGING_LABEL = {
  sfuso: 'Sfuso',
  cassette: 'In cassette',
  bins: 'Bins',
  sacchi: 'In sacchi',
  contenitori_industriali: 'Contenitori industriali',
};

export default function PublishBatch() {
  const nav = useNavigate();
  const [meta, setMeta] = useState({ fruit_types: [], quality_grades: [], packaging_types: [] });
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(null); // { batch_id, title }
  const [form, setForm] = useState({
    fruit_type: '',
    variety: '',
    quantity_quintals: '',
    price_per_quintal: '',
    total_price: '',
    quality_grade: 'standard',
    packaging: 'sfuso',
    harvest_date: '',
    available_from: '',
    available_until: '',
    description: '',
    location: { region: '', comune: '', address: '', lat: 42.5, lng: 12.5 },
    images: [],
    negotiable: true,
  });

  useEffect(() => { api.get('/batches/meta').then(r => setMeta(r.data)); }, []);

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setLoc = (k, v) => setForm(f => ({ ...f, location: { ...f.location, [k]: v } }));

  // Auto-compute total price when qty × unit change (only if user hasn't overridden total)
  const qty = parseFloat(form.quantity_quintals) || 0;
  const ppq = parseFloat(form.price_per_quintal) || 0;
  const autoTotal = qty && ppq ? (qty * ppq).toFixed(2) : '';

  const upload = async (files) => {
    const arr = [];
    for (const f of files) {
      const fd = new FormData();
      fd.append('file', f);
      try {
        const { data } = await api.post('/upload/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        arr.push(data.file_id);
      } catch (e) { toast.error(formatApiError(e)); }
    }
    setForm(f => ({ ...f, images: [...f.images, ...arr] }));
  };

  const submit = async () => {
    if (!form.fruit_type) return toast('Seleziona il tipo di prodotto');
    if (!form.quantity_quintals) return toast('Indica la quantità in quintali');
    if (!form.price_per_quintal && !form.total_price) return toast('Indica almeno un prezzo (per quintale o totale)');
    setBusy(true);
    try {
      const payload = {
        ...form,
        quantity_quintals: Number(form.quantity_quintals),
        price_per_quintal: form.price_per_quintal ? Number(form.price_per_quintal) : null,
        total_price: form.total_price ? Number(form.total_price) : null,
        harvest_date: form.harvest_date || null,
        available_from: form.available_from || null,
        available_until: form.available_until || null,
        location: form.location.comune || form.location.region ? {
          ...form.location,
          lat: Number(form.location.lat), lng: Number(form.location.lng),
        } : null,
      };
      const { data } = await api.post('/batches', payload);
      setSuccess({ batch_id: data.batch_id, title: form.title });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-12">
      <div className="text-xs uppercase tracking-[0.2em] font-bold text-terracotta mb-3">Pubblica una partita</div>
      <h1 className="font-display text-4xl md:text-5xl tracking-tighter flex items-center gap-3">
        <Leaf size={40} strokeWidth={1.4} className="text-chartreuse"/> Nuova partita
      </h1>
      <p className="mt-2 text-sm text-muted-foreground">Descrivi il tuo raccolto: qualità, quantità e prezzo. Sarà visibile subito a tutti i compratori.</p>

      <div className="mt-10 rounded-2xl border border-border p-8 bg-card space-y-6">

        <section>
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">1 · Prodotto</div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label>Tipo prodotto *</Label>
              <select
                value={form.fruit_type}
                onChange={e=>setF('fruit_type', e.target.value)}
                data-testid="pub-batch-fruit"
                className="mt-1.5 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm h-10"
              >
                <option value="">Seleziona…</option>
                {meta.fruit_types.map(t=><option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <Label>Varietà / cultivar</Label>
              <Input
                data-testid="pub-batch-variety"
                value={form.variety}
                onChange={e=>setF('variety', e.target.value)}
                placeholder="Es. Sangiovese, Nocellara del Belice"
              />
            </div>
          </div>
        </section>

        <section>
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">2 · Quantità e prezzo</div>
          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <div className="flex items-center justify-between gap-2">
                <Label className="flex items-center gap-1"><Weight size={12}/> Quintali *</Label>
                <a
                  href="/stima-quintali"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="pub-batch-ai-estimate-link"
                  className="text-[10px] uppercase tracking-widest font-bold text-terracotta hover:underline"
                >
                  ✨ Stima con AI
                </a>
              </div>
              <Input
                data-testid="pub-batch-qty"
                type="number"
                step="0.1"
                value={form.quantity_quintals}
                onChange={e=>setF('quantity_quintals', e.target.value)}
                placeholder="Es. 85"
              />
            </div>
            <div>
              <Label className="flex items-center gap-1"><Euro size={12}/> Prezzo per quintale *</Label>
              <Input
                data-testid="pub-batch-price-q"
                type="number"
                step="0.01"
                value={form.price_per_quintal}
                onChange={e=>setF('price_per_quintal', e.target.value)}
                placeholder="Es. 95"
              />
            </div>
            <div>
              <Label className="flex items-center gap-1"><Euro size={12}/> Totale partita</Label>
              <Input
                data-testid="pub-batch-price-tot"
                type="number"
                step="0.01"
                value={form.total_price || autoTotal}
                onChange={e=>setF('total_price', e.target.value)}
                placeholder={autoTotal ? `Auto: ${autoTotal}` : 'Facoltativo'}
              />
              {autoTotal && (
                <div className="mt-1.5 text-[10px] text-muted-foreground">
                  Calcolato automaticamente: {qty} q × € {ppq} = <b className="text-foreground">€ {autoTotal}</b>
                </div>
              )}
            </div>
          </div>
          <label className="mt-3 flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
            <input
              type="checkbox"
              checked={form.negotiable}
              onChange={e=>setF('negotiable', e.target.checked)}
              data-testid="pub-batch-negotiable"
            />
            Prezzo trattabile
          </label>
        </section>

        <section>
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">3 · Qualità e confezionamento</div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label>Qualità</Label>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {meta.quality_grades.map(qg => (
                  <button
                    key={qg}
                    type="button"
                    onClick={()=>setF('quality_grade', qg)}
                    data-testid={`pub-batch-quality-${qg}`}
                    className={`text-xs px-3 py-1.5 rounded-full border ${form.quality_grade===qg?'bg-foreground text-background border-foreground':'border-border hover:bg-secondary'}`}
                  >
                    {QUALITY_LABEL[qg]}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="flex items-center gap-1"><Package size={12}/> Confezionamento</Label>
              <select
                value={form.packaging}
                onChange={e=>setF('packaging', e.target.value)}
                data-testid="pub-batch-packaging"
                className="mt-1.5 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm h-10"
              >
                {meta.packaging_types.map(p=><option key={p} value={p}>{PACKAGING_LABEL[p] || p}</option>)}
              </select>
            </div>
          </div>
        </section>

        <section>
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">4 · Tempistiche</div>
          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <Label className="flex items-center gap-1"><Calendar size={12}/> Data raccolto</Label>
              <Input type="date" value={form.harvest_date} onChange={e=>setF('harvest_date', e.target.value)}/>
            </div>
            <div>
              <Label>Disponibile dal</Label>
              <Input type="date" value={form.available_from} onChange={e=>setF('available_from', e.target.value)}/>
            </div>
            <div>
              <Label>Disponibile fino al</Label>
              <Input type="date" value={form.available_until} onChange={e=>setF('available_until', e.target.value)}/>
            </div>
          </div>
        </section>

        <section>
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">5 · Localizzazione</div>
          <div className="grid md:grid-cols-3 gap-3">
            <div><Label>Regione</Label><Input value={form.location.region} onChange={e=>setLoc('region', e.target.value)}/></div>
            <div><Label>Comune</Label><Input value={form.location.comune} onChange={e=>setLoc('comune', e.target.value)}/></div>
            <div><Label>Indirizzo (opzionale)</Label><Input value={form.location.address} onChange={e=>setLoc('address', e.target.value)}/></div>
          </div>
        </section>

        <section>
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">6 · Descrizione</div>
          <Textarea
            rows={4}
            value={form.description}
            onChange={e=>setF('description', e.target.value)}
            placeholder="Note su origine, trattamenti, certificazioni, condizioni di consegna…"
            data-testid="pub-batch-description"
          />
        </section>

        <section>
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">7 · Foto (opzionali)</div>
          <label className="rounded-2xl border-2 border-dashed border-border p-8 grid place-items-center cursor-pointer hover:bg-secondary/40">
            <Upload size={22}/>
            <div className="mt-2 text-sm">Trascina o clicca per caricare</div>
            <input type="file" className="hidden" accept="image/*" multiple onChange={e=>upload(Array.from(e.target.files||[]))}/>
          </label>
          {form.images.length > 0 && (
            <div className="mt-3 grid grid-cols-3 md:grid-cols-4 gap-3">
              {form.images.map(id => (
                <div key={id} className="relative">
                  <img src={imgUrl(id)} alt="" className="aspect-square w-full object-cover rounded-xl border border-border"/>
                  <button onClick={()=>setForm(f=>({...f, images: f.images.filter(x=>x!==id)}))}
                    className="absolute top-1 right-1 rounded-full bg-background border border-border p-1"><X size={12}/></button>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      <div className="mt-6 flex justify-between">
        <Button variant="ghost" onClick={()=>nav(-1)}>Annulla</Button>
        <Button
          onClick={submit}
          disabled={busy}
          data-testid="pub-batch-submit"
          className="rounded-full"
        >
          {busy ? 'Pubblicazione…' : 'Pubblica partita'}
        </Button>
      </div>
      <PublishSuccessModal
        open={!!success}
        onClose={() => { setSuccess(null); nav('/partite'); }}
        title="Partita pubblicata!"
        subtitle={success?.title ? `"${success.title}" è già visibile a tutti sul marketplace.` : null}
        primaryCta={success ? { label: 'Vedi la partita', to: `/partite/${success.batch_id}` } : null}
        secondaryCta={{ label: 'Torna al marketplace', to: '/partite' }}
      />
    </div>
  );
}
